import { useEffect } from 'react';
import styled from '@emotion/styled';
import {useDispatch, useSelector} from '../hooks';
import analytics from 'shared/utility/analytics';
import {Hyperlink} from 'shared/buttons';
import {Heading, Info, LabelText} from 'shared/components';
import {Checkbox, CheckboxWithCurrencyInput, CurrencyInput} from 'shared/inputs';
import {InputsContainer} from 'shared/containers';
import {sync, SYNC} from 'shared/wizard/actions';

const SubExpenseForm = styled.div`
  ${LabelText} {
    margin-top: 8px;
  }
  > div:last-of-type {
    margin-bottom: 16px;
  }
`;

const WizardExpenses = () => {
  const dispatch = useDispatch();
  const {
    input: {
      applicant_has_partner,
      has_student_loan,
      student_loan_monthly_payment,
      has_partner_student_loan,
      partner_student_loan_monthly_payment,
      has_private_lease,
      private_lease,
      has_partner_private_lease,
      partner_private_lease,
    },
    output: {errors},
  } = useSelector(state => state);

  useEffect(() => {
    dispatch({type: SYNC, syncing: false});
    analytics.send('Navigeer', 'Financiële verplichtingen');
  }, []);

  return (
    <>
      <Heading>Financi&#xEB;le verplichtingen</Heading>
      <InputsContainer>
        <LabelText>
          Welke van de onderstaande verplichtingen gelden voor jou{applicant_has_partner ? ' en je partner' : ''}?
        </LabelText>
        <div css={{display: 'flex'}}>
          <Checkbox
            name="has_student_loan"
            label="Ik heb een studieschuld"
            value={has_student_loan}
            onChange={sync(dispatch)('has_student_loan')}
            autoFocus
          />
          <Info title="Totaal studieschuld">
            Vul hier de maandelijkse betaling van je studieschuld in.
            Wat de maandelijkse betaling van je studieschuld is vind je op{' '}
            <Hyperlink href="https://mijnduo.nl">mijnduo.nl</Hyperlink>.
          </Info>
        </div>
        {has_student_loan ? (
          <SubExpenseForm>
            <LabelText>Wat zijn de maandlasten van jouw studieschuld?</LabelText>
            <CurrencyInput
              onChange={sync(dispatch)('student_loan_monthly_payment')}
              value={student_loan_monthly_payment}
              errors={Boolean(errors?.student_loan_monthly_payment)}
              placeholder="Maandelijks bedrag"
            />
          </SubExpenseForm>
        ) : null}
        {applicant_has_partner ? (
          <>
            <div css={{display: 'flex'}}>
              <Checkbox
                name="has_partner_student_loan"
                label="Mijn partner heeft een studieschuld"
                value={has_partner_student_loan}
                onChange={sync(dispatch)('has_partner_student_loan')}
              />
              <Info title="Totaal studieschuld">
                Vul hier de maandelijkse betaling van je studieschuld in.
                Wat de maandelijkse betaling van je studieschuld is vind je op{' '}
                <Hyperlink href="https://mijnduo.nl">mijnduo.nl</Hyperlink>.
              </Info>
            </div>
            {has_partner_student_loan ? (
              <SubExpenseForm>
                <LabelText>Wat zijn de studieschuld maandlasten van je partner?</LabelText>
                <CurrencyInput
                    onChange={sync(dispatch)('partner_student_loan_monthly_payment')}
                    value={partner_student_loan_monthly_payment}
                    errors={Boolean(errors?.partner_student_loan_monthly_payment)}
                    placeholder="Maandelijks bedrag"
                />
              </SubExpenseForm>
            ) : null}
          </>
        ) : null}

        <CheckboxWithCurrencyInput name="total_credits" label="Ik heb een krediet">
          <Info title="Totaal kredieten">
            Vul hier het totaal van jouw uitstaande leningen in.
          </Info>
        </CheckboxWithCurrencyInput>
        {applicant_has_partner ? (
          <CheckboxWithCurrencyInput name="partner_total_credits" label="Mijn partner heeft een krediet">
            <Info title="Totaal kredieten">
              Vul hier het totaal van je partners uitstaande leningen in.
            </Info>
          </CheckboxWithCurrencyInput>
        ) : null}
        <CheckboxWithCurrencyInput
          name="partner_alimony"
          label="Ik betaal partneralimentatie"
          placeholder="Bedrag (per maand)"
        >
          <Info title="Alimentatie aan partner">
            Hiermee bedoelen we alimentatie aan de ex-partner (niet aan de kinderen).
          </Info>
        </CheckboxWithCurrencyInput>
        {applicant_has_partner ? (
          <CheckboxWithCurrencyInput
            name="partner_partner_alimony"
            label="Mijn partner betaalt partneralimentatie"
            placeholder="Bedrag (per maand)"
          >
            <Info title="Alimentatie aan partner">
              Hiermee bedoelen we alimentatie aan de ex-partner (niet aan de kinderen).
            </Info>
          </CheckboxWithCurrencyInput>
        ) : null}
        <CheckboxWithCurrencyInput name="ground_rent" label="Erfpacht (per jaar)">
          <Info title="Erfpacht van de woning die je wilt kopen">
            Bij sommige woningen is de grond niet je eigendom, maar &apos;huur&apos; je de grond waar de woning op
            staat. Dat heet erfpacht. Vul hier de jaarlijkse erfpachtkosten in.
          </Info>
        </CheckboxWithCurrencyInput>
        <div css={{display: 'flex'}}>
          <Checkbox
            name="has_private_lease"
            label="Ik heb een private lease"
            value={has_private_lease}
            onChange={sync(dispatch)('has_private_lease')}
          />
          <Info title="Private lease">
            Met private lease rijd je als particulier een nieuwe auto tegen een
            vast maandbedrag zonder dat deze van jou is. Vul hier dat
            maandbedrag in.
          </Info>
        </div>
        {has_private_lease && (
            <SubExpenseForm>
              <LabelText>Bedrag per maand</LabelText>
              <CurrencyInput
                id="private_lease"
                onChange={sync(dispatch)('private_lease')}
                value={private_lease}
                errors={errors?.private_lease}
                placeholder="Bedrag per maand"
              />
            </SubExpenseForm>
        )}
        {applicant_has_partner ? (
          <>
            <div css={{display: 'flex'}}>
              <Checkbox
                name="has_partner_private_lease"
                label="Mijn partner heeft een private lease"
                value={has_partner_private_lease}
                onChange={sync(dispatch)('has_partner_private_lease')}
              />
              <Info title="Private lease">
                Met private lease rijd je als particulier een nieuwe auto tegen een
                vast maandbedrag zonder dat deze van jou is. Vul hier dat
                maandbedrag in.
              </Info>
            </div>
          {has_partner_private_lease ? (
            <SubExpenseForm>
              <LabelText>Bedrag per maand</LabelText>
              <CurrencyInput
                onChange={sync(dispatch)('partner_private_lease')}
                value={partner_private_lease}
                errors={errors?.partner_private_lease}
                placeholder="Bedrag per maand"
              />
            </SubExpenseForm>
            ) : null}
          </>
        ) : null}
      </InputsContainer>
    </>
  );
};

export default WizardExpenses;
